import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OperatorsService } from '../operators/operators.service';
import { isOperatorEmtpy, Operator } from '../model/Operator';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { OperatorAddStationComponent } from '../operator-add-station/operator-add-station.component';
import { HttpStatusCode } from '@angular/common/http';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  switchMap,
  take
} from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'met-operator-view',
  templateUrl: './operator-view.component.html',
  styleUrls: ['./operator-view.component.scss']
})
export class OperatorViewComponent implements OnInit {
  operatorId: string | null = '';
  operator: Operator = {
    operatorId: 0,
    vhOperatorId: '',
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    postCode: '',
    phoneNumber: '',
    email: '',
    stationMap: new Map<string, string>()
  };

  private stations: Map<string, string> = new Map<string, string>();
  status = false;
  private isNewOperator = true;

  deletable = false;
  private rootStationId: string | null = null;
  private rootStationName: string | null = null;
  searchId = new FormControl<string | Operator | null>('');
  searchFirstName = new FormControl<string | Operator | null>('');
  searchLastName = new FormControl<string | Operator | null>('');
  filteredOperators$: Observable<Operator[]> = of([]);
  private noResults$ = new BehaviorSubject<boolean>(false);
  showNoResults$ = this.noResults$.asObservable();
  private dialogRef: MatDialogRef<ConfirmationDialogComponent> | undefined;

  constructor(
    private route: ActivatedRoute,
    private operatorService: OperatorsService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.operatorService.searchStations('').subscribe(stations => {
      this.stations = new Map(Object.entries(stations));
    });
    this.rootStationId = this.route.snapshot.paramMap.get('stationId');
    this.rootStationName = this.route.snapshot.paramMap.get('stationName');
    if (this.rootStationId != null && this.rootStationName != null) {
      this.operator.stationMap.set(this.rootStationId, this.rootStationName);
      this.deletable = this.operator.stationMap.size > 0;
    }
    this.route.paramMap.subscribe(pm => {
      this.operatorId = pm.get('operatorId');

      if (this.operatorId && this.operatorId !== '0') {
        this.operatorService
          .getOperator(parseInt(this.operatorId, 10))
          .pipe(take(1))
          .subscribe(op => {
            this.operator = op;
            this.operator.stationMap = new Map(Object.entries(op.stationMap));
            this.isNewOperator = isOperatorEmtpy(this.operator);
            this.checkMap();
            this.patchSearchControlsFromOperator();
          });
      } else {
        this.patchSearchControlsFromOperator();
      }
    });

    this.filteredOperators$ = combineLatest([
      this.searchId.valueChanges.pipe(startWith(this.searchId.value)),
      this.searchFirstName.valueChanges.pipe(startWith(this.searchFirstName.value)),
      this.searchLastName.valueChanges.pipe(startWith(this.searchLastName.value))
    ]).pipe(
      debounceTime(250),
      map(([idVal, fnVal, lnVal]) => {
        const id = this.toText(idVal, 'id').trim();
        const fn = this.toText(fnVal, 'first').trim();
        const ln = this.toText(lnVal, 'last').trim();
        return {id, fn, ln};
      }),
      distinctUntilChanged((a, b) => a.id === b.id && a.fn === b.fn && a.ln === b.ln),
      switchMap(({id, fn, ln}) => {
        const vhId = (id ?? '').trim();
        const first = (fn ?? '').trim();
        const last = (ln ?? '').trim();

        const hasId    = vhId.length >= 2;
        const hasNames = first.length >= 2 || last.length >= 2;

        if (!hasId && !hasNames) {
          this.noResults$.next(false);
          return of<Operator[]>([]);
        }

        const base = { limit: 10, offset: 0, mode: 'light' } as const;

        const nameOnly: any = { ...base };
        if (first.length >= 2) nameOnly.firstName = first;
        if (last.length  >= 2) nameOnly.lastName  = last;

        if (!hasId) {
          return this.operatorService.searchOperators(nameOnly)
            .pipe(catchError(() => of<Operator[]>([])));
        }

        const withAll: any = { ...nameOnly, vhOperatorId: vhId };

        return this.operatorService.searchOperators(withAll).pipe(
          catchError(() => of<Operator[]>([])),
          switchMap(list => {
            if (list?.length) return of(list);
            return hasNames
              ? this.operatorService.searchOperators(nameOnly).pipe(catchError(() => of<Operator[]>([])))
              : of<Operator[]>([]);
          })
        );
      }),
      map(list => {
        this.noResults$.next((list ?? []).length === 0);
        return list ?? [];
      })
    );
  }

  private patchSearchControlsFromOperator(): void {
    this.searchId.setValue(this.operator.vhOperatorId ?? '', { emitEvent: false });
    this.searchFirstName.setValue(this.operator.firstName ?? '', { emitEvent: false });
    this.searchLastName.setValue(this.operator.lastName ?? '', { emitEvent: false });
  }

  displayOperator = (v: any): string => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    const ln = v.lastName ?? '';
    const fn = v.firstName ?? '';
    const id = v.vhOperatorId ?? '';
    const name = [ln, fn].filter(Boolean).join(', ');
    return id ? (name ? `${name} · ${id}` : id) : name;
  };

  onOperatorPicked(picked: Operator) {
    const stationMap =
      picked.stationMap instanceof Map
        ? new Map<string, string>(picked.stationMap as any)
        : new Map<string, string>(Object.entries((picked as any).stationMap ?? {}));

    const isAddPage = !this.operatorId || this.operatorId === '0';

    this.operator = {
      ...picked,
      operatorId: isAddPage ? 0 : picked.operatorId,
      vhOperatorId: picked.vhOperatorId,
      stationMap
    };

    this.patchSearchControlsFromOperator();

    this.isNewOperator = isAddPage ? true : isOperatorEmtpy(this.operator);
    this.checkMap();
  }

  changeValue(property: string, $event: string) {
    this.status = false;
    switch (property) {
      case 'id':
        this.operator.vhOperatorId = $event;
        break;
      case 'firstName':
        this.operator.firstName = $event;
        break;
      case 'lastName':
        this.operator.lastName = $event;
        break;
      case 'phoneNumber':
        this.operator.phoneNumber = $event;
        break;
      case 'email':
        this.operator.email = $event;
        break;
      case 'street':
        this.operator.street = $event;
        break;
      case 'postCode':
        this.operator.postCode = $event;
        break;
      case 'city':
        this.operator.city = $event;
        break;
    }
  }

  persist(mode?: 'create' | 'update'): void {
    const willCreate = mode ? mode === 'create' : !this.operator?.operatorId || this.operator.operatorId === 0;
    const payload = willCreate ? { ...this.operator, operatorId: 0 } : this.operator;

    const req$ = willCreate ? this.operatorService.addOperator(payload) : this.operatorService.saveOperator(payload);

    req$.subscribe({
      next: result => {
        this.status = result.status === HttpStatusCode.Ok || result.status === HttpStatusCode.Created;
        if (this.status) {
          const sm: any = this.operator.stationMap;
          this.operator.stationMap =
            sm instanceof Map ? sm : new Map<string, string>(Object.entries(sm ?? {}));
          this.checkMap();
        }
      },
      error: () => {
        this.status = false;
      }
    });
  }

  private openSaveWarningDialog(message: string, onConfirm: () => void): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.data = message;

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) onConfirm();
      this.dialogRef = undefined;
    });
  }

  save() {
    this.status = false;

    const id = (this.operator.vhOperatorId ?? '').trim();
    const firstname = (this.operator.firstName ?? '').trim();
    const lastName = (this.operator.lastName ?? '').trim();

    this.operatorService
      .searchOperators({
        vhOperatorId: id || undefined,
        lastName: lastName || undefined,
        firstName: firstname || undefined,
        mode: 'full',
        limit: 10,
        offset: 0
      })
      .pipe(take(1))
      .subscribe({
        next: (list: Operator[]) => {
          const results = list ?? [];
          const lower = (s?: string) => (s ?? '').trim().toLowerCase();

          const exactDup = !!id && results.some(x =>
            x.operatorId !== this.operator.operatorId &&
            (x.vhOperatorId ?? '').trim() === id &&
            lower(x.firstName) === lower(firstname) &&
            lower(x.lastName) === lower(lastName)
          );

          if (exactDup) {
            this.openSaveWarningDialog('Dieser Beitrag existiert schon. Trotzdem speichern?', () => this.persist());
            return;
          }
          this.persist();
        },
        error: () => {
          this.persist();
        }
      });
  }


  deleteStation(stationKey: string) {
    this.status = false;
    this.operator.stationMap.delete(stationKey);
  }

  openAddStation() {
    this.status = false;
    this.dialog
      .open(OperatorAddStationComponent, {
        data: { stations: this.stations }
      })
      .afterClosed()
      .subscribe(stationName => {
        if (stationName != undefined) {
          for (const [key, value] of this.stations.entries()) {
            if (value === stationName.data) {
              this.operator.stationMap.set(key, value);
              return;
            }
          }
        }
      });
  }

  openDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.data = 'Möchten Sie den Betreiber wirklich löschen?';

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);

    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.operatorService.deleteOperator(this.operator.operatorId).subscribe(ok => {
          if (ok) this.router.navigate(['operators']);
        });
      }
      this.dialogRef = undefined;
    });
  }

  checkMap() {
    const existing = !!this.operator?.operatorId && this.operator.operatorId > 0;
    const sm: any = this.operator.stationMap as any;
    const size = sm instanceof Map ? sm.size : Object.keys(sm ?? {}).length;
    this.deletable = existing && size === 0;
  }


  onNameInput(field: 'id' | 'firstName' | 'lastName', ev: Event) {
    const value = (ev.target as HTMLInputElement)?.value ?? '';
    this.changeValue(field, value);
  }

  toText(v: string | Operator | null | undefined, pick: 'id' | 'first' | 'last'): string {
    if (typeof v === 'string') return v;
    if (v && typeof v === 'object') {
      if (pick === 'id') return (v as any).vhOperatorId ?? '';
      if (pick === 'first') return (v as any).firstName ?? '';
      return (v as any).lastName ?? '';
    }
    return '';
  }

}
